import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { useTenant } from '../../hooks/useTenant';
import type { JobsResponse, JobDetailResponse, JobStatsResponse, JobsParams } from '../types';
import { requireParam, requireTenant } from './queryGuards';

export function useJobs(params: Omit<JobsParams, 'tenant_id'> = {}) {
  const { tenantId } = useTenant();

  return useQuery<JobsResponse>({
    queryKey: ['jobs', tenantId, params],
    queryFn: () => {
      const requiredTenantId = requireTenant(tenantId);
      return apiClient.getJobs({ tenant_id: requiredTenantId, ...params });
    },
    enabled: !!tenantId,
  });
}

export function useJobDetail(jobId: string | undefined, includeLogs = false) {
  const { tenantId } = useTenant();

  return useQuery<JobDetailResponse>({
    queryKey: ['job', jobId, tenantId, includeLogs],
    queryFn: () => {
      const requiredTenantId = requireTenant(tenantId);
      const requiredJobId = requireParam(jobId, 'No job ID');
      return apiClient.getJobDetail(requiredJobId, requiredTenantId, includeLogs);
    },
    enabled: !!tenantId && !!jobId,
  });
}

export function useJobStats(jobId: string | undefined) {
  const { tenantId } = useTenant();

  return useQuery<JobStatsResponse>({
    queryKey: ['job-stats', jobId, tenantId],
    queryFn: () => {
      const requiredTenantId = requireTenant(tenantId);
      const requiredJobId = requireParam(jobId, 'No job ID');
      return apiClient.getJobStats(requiredJobId, requiredTenantId);
    },
    enabled: !!tenantId && !!jobId,
  });
}
